import { ENTITY_TAG_NAMESPACE, entityTagsOf, parseEntityTag, type EntityTagValue } from './entityTags'

export const CATEGORY_TAG_NAMESPACE = 'category'
export const KIND_TAG_NAMESPACE = 'kind'

export type TagHue = 'blue' | 'violet' | 'green' | 'amber' | 'teal' | 'pink' | 'indigo' | 'slate'

export const TAG_HUES: readonly TagHue[] = ['blue', 'violet', 'green', 'amber', 'teal', 'pink', 'indigo', 'slate']

export const tagHueClass = (hue: TagHue): string => `zocks-tag-${hue}`

export const tagHueBorderClass = (hue: TagHue): string => `zocks-tag-border-${hue}`

export type WorkflowCategoryKey = 'meeting-lifecycle' | 'client-lifecycle' | 'growth' | 'compliance' | 'productivity' | 'marketing'

export const WORKFLOW_CATEGORY_KEYS: readonly WorkflowCategoryKey[] = [
  'meeting-lifecycle',
  'client-lifecycle',
  'growth',
  'compliance',
  'productivity',
  'marketing',
]

export interface WorkflowCategoryInfo {
  label: string
  icon: string
  hue: TagHue
}

export const WORKFLOW_CATEGORIES: Record<WorkflowCategoryKey, WorkflowCategoryInfo> = {
  'meeting-lifecycle': {
    label: 'Meeting Lifecycle',
    icon: 'bi-calendar-event',
    hue: 'blue',
  },
  'client-lifecycle': {
    label: 'Client Lifecycle',
    icon: 'bi-people',
    hue: 'violet',
  },
  growth: {
    label: 'Growth & Opportunities',
    icon: 'bi-graph-up-arrow',
    hue: 'green',
  },
  compliance: {
    label: 'Monitoring, Risk & Compliance',
    icon: 'bi-shield-check',
    hue: 'amber',
  },
  productivity: {
    label: 'Advisor Productivity',
    icon: 'bi-lightning-charge',
    hue: 'teal',
  },
  marketing: { label: 'Marketing', icon: 'bi-megaphone', hue: 'pink' },
}

export const UNCATEGORIZED_ICON = 'bi-diagram-3'
export const UNCATEGORIZED_HUE: TagHue = 'slate'

export type WorkflowKind = 'workflow' | 'agent'

export const WORKFLOW_KINDS: readonly WorkflowKind[] = ['workflow', 'agent']

export const WORKFLOW_KIND_LABELS: Record<WorkflowKind, string> = {
  workflow: 'Workflow',
  agent: 'Agent',
}

export const WORKFLOW_KIND_ICONS: Record<WorkflowKind, string> = {
  workflow: 'bi-diagram-2',
  agent: 'bi-stars',
}

const ENTITY_CATEGORY_FALLBACK: Record<EntityTagValue, WorkflowCategoryKey> = {
  contact: 'client-lifecycle',
  meeting: 'meeting-lifecycle',
  ccq: 'client-lifecycle',
  document: 'productivity',
  insights: 'productivity',
}

const KNOWN_NAMESPACES: readonly string[] = [ENTITY_TAG_NAMESPACE, CATEGORY_TAG_NAMESPACE, KIND_TAG_NAMESPACE]

interface NamespacedTag {
  namespace: string
  value: string
}

const asNamespacedTag = (tag: string): NamespacedTag | null => {
  const parts = tag.toLowerCase().split(':')
  if (parts.length !== 2) {
    return null
  }
  const [namespace, value] = parts
  if (!KNOWN_NAMESPACES.includes(namespace) || value.length === 0) {
    return null
  }
  return { namespace, value }
}

const isCategoryKey = (value: string): value is WorkflowCategoryKey => WORKFLOW_CATEGORY_KEYS.some((known) => known === value)

const isWorkflowKind = (value: string): value is WorkflowKind => WORKFLOW_KINDS.some((known) => known === value)

const explicitCategory = (tags: NamespacedTag[]): WorkflowCategoryKey | null =>
  tags.reduce<WorkflowCategoryKey | null>(
    (found, tag) => found ?? (tag.namespace === CATEGORY_TAG_NAMESPACE && isCategoryKey(tag.value) ? tag.value : null),
    null,
  )

const explicitKind = (tags: NamespacedTag[]): WorkflowKind | null =>
  tags.reduce<WorkflowKind | null>((found, tag) => found ?? (tag.namespace === KIND_TAG_NAMESPACE && isWorkflowKind(tag.value) ? tag.value : null), null)

const tagWasConsumed = (tag: string, consumedCategory: WorkflowCategoryKey | null, consumedKind: WorkflowKind, consumedEntities: EntityTagValue[]): boolean => {
  const namespaced = asNamespacedTag(tag)
  if (namespaced === null) {
    return false
  }
  if (namespaced.namespace === CATEGORY_TAG_NAMESPACE && isCategoryKey(namespaced.value) && consumedCategory === namespaced.value) {
    return true
  }
  if (namespaced.namespace === KIND_TAG_NAMESPACE && isWorkflowKind(namespaced.value) && consumedKind === namespaced.value) {
    return true
  }
  if (namespaced.namespace === ENTITY_TAG_NAMESPACE) {
    const entityValue = parseEntityTag(tag)
    if (entityValue !== null && consumedEntities.includes(entityValue)) {
      return true
    }
  }
  return false
}

export interface ParsedWorkflowTags {
  category: WorkflowCategoryKey | null
  kind: WorkflowKind
  entities: EntityTagValue[]
  hashtags: string[]
}

export const parseWorkflowTags = (workflow: { tags: string[] }): ParsedWorkflowTags => {
  const namespaced = workflow.tags.reduce<NamespacedTag[]>((acc, tag) => {
    const parsed = asNamespacedTag(tag)
    return parsed ? [...acc, parsed] : acc
  }, [])
  const entities = entityTagsOf(workflow)
  const fallback = entities.length > 0 ? ENTITY_CATEGORY_FALLBACK[entities[0]] : null
  const category = explicitCategory(namespaced) ?? fallback
  const kind = explicitKind(namespaced) ?? 'workflow'
  return {
    category,
    kind,
    entities,
    hashtags: workflow.tags.filter((tag) => !tagWasConsumed(tag, category, kind, entities)),
  }
}

export const hueForTag = (tag: string): TagHue => {
  const hash = tag
    .toLowerCase()
    .split('')
    .reduce((acc, character) => (acc * 31 + character.charCodeAt(0)) % 1000003, 7)
  return TAG_HUES[hash % TAG_HUES.length]
}
